import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import DichotomyStage from "./DichotomyStage.jsx";
import MorningSystemStage from "./MorningSystemStage.jsx";
import PauseStage from "./PauseStage.jsx";
import DebriefStage from "./DebriefStage.jsx";
import "./InnerCitadel.css";

const STAGES = ["intro", "dichotomy", "system", "pause", "debrief"];
const STAGE_LABELS = { dichotomy: "Dichotomy", system: "System", pause: "The Pause", debrief: "Report" };

export default function InnerCitadel() {
  const navigate = useNavigate();
  const [stage, setStage] = useState("intro");
  const [dichotomyResult, setDichotomyResult] = useState(null);
  const [systemPlan, setSystemPlan] = useState(null);
  const [pauseResult, setPauseResult] = useState(null);

  const restart = () => {
    setDichotomyResult(null);
    setSystemPlan(null);
    setPauseResult(null);
    setStage("intro");
  };

  return (
    <div className="mx-auto max-w-2xl px-4 py-8">
      <button onClick={() => navigate("/")} className="btn-ghost mb-6 !text-[8px]">
        ◀ BACK TO LEVELS
      </button>

      <div className="panel relative overflow-hidden p-6">
        <div className="mb-6">
          <p className="label-pixel text-lime">Level 08</p>
          <h1 className="pixel-heading text-base text-mist sm:text-xl">The Inner Citadel</h1>
        </div>

        {stage !== "intro" && (
          <div className="mb-6 flex gap-1.5">
            {["dichotomy", "system", "pause", "debrief"].map((s) => {
              const idx = STAGES.indexOf(s);
              const currentIdx = STAGES.indexOf(stage);
              const complete = idx < currentIdx;
              const active = s === stage;
              return (
                <div key={s} className="flex-1">
                  <div
                    className={`h-1.5 w-full border border-black/60 ${
                      complete ? "bg-lime" : active ? "bg-cyan" : "bg-panel2"
                    }`}
                  />
                  <p
                    className={`mt-1 text-center font-display text-[7px] uppercase ${
                      active ? "text-cyan" : complete ? "text-lime" : "text-mist/30"
                    }`}
                  >
                    {STAGE_LABELS[s]}
                  </p>
                </div>
              );
            })}
          </div>
        )}

        <div className="ic-panel-transition" key={stage}>
          {stage === "intro" && (
            <div className="text-center">
              <p className="mb-3 text-sm text-mist/80">
                Every other level in this app is about money or time. This one's about the thing
                underneath both: how you actually react when something doesn't go your way — a
                bad grade, a rude comment, a plan falling apart. That reaction is trainable, same
                as anything else here.
              </p>
              <p className="mb-6 text-xs text-mist/50">
                Three phases: sort what's actually yours to control, build a small daily system,
                then live through a handful of moments that test the gap between a stimulus and a
                response.
              </p>
              <button onClick={() => setStage("dichotomy")} className="btn-cyan">
                ▶ BEGIN THE SORT
              </button>
            </div>
          )}

          {stage === "dichotomy" && (
            <DichotomyStage
              onComplete={(result) => {
                setDichotomyResult(result);
                setStage("system");
              }}
            />
          )}

          {stage === "system" && (
            <MorningSystemStage
              onComplete={(plan) => {
                setSystemPlan(plan);
                setStage("pause");
              }}
            />
          )}

          {stage === "pause" && systemPlan && (
            <PauseStage
              onComplete={(result) => {
                setPauseResult(result);
                setStage("debrief");
              }}
            />
          )}

          {stage === "debrief" && dichotomyResult && systemPlan && pauseResult && (
            <DebriefStage
              dichotomyResult={dichotomyResult}
              systemPlan={systemPlan}
              pauseResult={pauseResult}
              onRetry={restart}
            />
          )}
        </div>
      </div>
    </div>
  );
}
